import { create } from 'zustand';
import produce from 'immer';
import { notEquals } from '@higo/common/lib/utils';

const toValidId = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export interface ExaminationsState {
  newExaminations: number[];
  takenExaminations: number[];

  actions: {
    addNewExamination: (examinationId: number | string) => void;
    removeNewExamination: (examinationId: number | string) => void;
    clearNewExaminations: () => void;

    addTakenExamination: (examinationId: number | string) => void;
    clearTakenExaminations: () => void;
  };
}

const examinationsStore = create<ExaminationsState>((set) => ({
  newExaminations: [],
  takenExaminations: [],

  actions: {
    addNewExamination: (examinationId) =>
      set(
        produce((state: ExaminationsState) => {
          const id = toValidId(examinationId);
          if (id === null) return;
          if (!state.newExaminations.includes(id)) {
            state.newExaminations.unshift(id);
          }
        }),
      ),

    removeNewExamination: (examinationId) =>
      set(
        produce((state: ExaminationsState) => {
          const id = toValidId(examinationId);
          if (id === null) return;
          state.newExaminations = state.newExaminations.filter(notEquals(id));
        }),
      ),

    clearNewExaminations: () =>
      set(
        produce((state: ExaminationsState) => {
          state.newExaminations = [];
        }),
      ),

    addTakenExamination: (examinationId) =>
      set(
        produce((state: ExaminationsState) => {
          const id = toValidId(examinationId);
          if (id === null) return;
          if (!state.takenExaminations.includes(id)) {
            state.takenExaminations.unshift(id);
          }
        }),
      ),

    clearTakenExaminations: () =>
      set(
        produce((state: ExaminationsState) => {
          state.takenExaminations = [];
        }),
      ),
  },
}));

export const useNewExaminations = () =>
  examinationsStore((state) => state.newExaminations);

export const useExaminationsStoreActions = () =>
  examinationsStore((state) => state.actions);

export const useTakenExaminations = () =>
  examinationsStore((state) => state.takenExaminations);
